/**
 * Partnership API Functions
 *
 * API functions for company partnership operations
 */

import type {
  Company,
  CompanyPartnership,
  CreatePartnershipDto,
  UpdatePartnershipDto,
  PartnershipStatus,
  PartnershipType,
} from '@lognai/plugin-admin';
import { apiFetch, buildQueryString } from './client';
import type { ListQueryParams } from '@lognai/frontend-core';

const BASE_URL = '/api/companies';

/**
 * Partnership with enriched data. The partner company is populated under
 * `partnerCompany` (populate-by-id `as: 'partnerCompany'`), matching the
 * backend EnrichedPartnership.
 */
export interface EnrichedPartnership extends CompanyPartnership {
  partnerCompany?: {
    id: string;
    name: string;
    slug: string;
  };
  ownerCompany?: {
    id: string;
    name: string;
    slug: string;
  };
  /**
   * The company on the OTHER side from the one asking. `partnerCompanyId` is only
   * that when the asker owns the partnership; set by the server so no consumer
   * has to repeat the direction logic.
   */
  counterpartyCompanyId?: string;
  counterpartyCompany?: {
    id: string;
    name: string;
    slug: string;
  };
}

/**
 * Paginated partnership list as returned by the HTTP endpoint:
 * `{ items, pagination: { page, limit, total, totalPages } }`.
 */
export interface PaginatedPartnershipList {
  items: EnrichedPartnership[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext?: boolean;
    hasPrev?: boolean;
  };
}

/**
 * Partnership filter fields
 */
export interface PartnershipFilter {
  status?: PartnershipStatus;
  partnerType?: PartnershipType;
}

/**
 * Partnership query params
 */
export type PartnershipQueryParams = ListQueryParams<PartnershipFilter>;

/**
 * Get list of partnerships for a company
 */
export const listPartnerships = async (
  companyId: string,
  params?: PartnershipQueryParams
): Promise<PaginatedPartnershipList> => {
  const query = buildQueryString(params as Record<string, unknown>);
  return apiFetch<PaginatedPartnershipList>(
    `${BASE_URL}/${companyId}/partnerships${query}`
  );
};

/**
 * Every partnership for a company, across pages.
 *
 * `listPartnerships` returns ONE page, and the server's default page is 20 rows
 * (max 100). A picker that read that single page could only ever offer a
 * company's first 20 partners — a partner added later was simply absent, with no
 * search or paging to find it. This follows `pagination.totalPages` to the end.
 * Capped, because "all of them" is not a promise worth an unbounded loop.
 */
export const MAX_ALL_PARTNERSHIPS_PAGES = 10;

export const listAllPartnerships = async (
  companyId: string,
  params: Omit<PartnershipQueryParams, 'page' | 'limit'> = {}
): Promise<PaginatedPartnershipList> => {
  const limit = 100;
  const first = await listPartnerships(companyId, { ...params, page: 1, limit });
  const items = [...first.items];
  const totalPages = Math.min(first.pagination?.totalPages ?? 1, MAX_ALL_PARTNERSHIPS_PAGES);
  for (let page = 2; page <= totalPages; page++) {
    const next = await listPartnerships(companyId, { ...params, page, limit });
    items.push(...next.items);
  }
  return {
    items,
    pagination: { ...first.pagination, page: 1, limit: items.length, totalPages: 1 },
  };
};

/**
 * Get a single partnership by ID
 */
export const getPartnership = async (
  companyId: string,
  partnershipId: string
): Promise<EnrichedPartnership> => {
  return apiFetch<EnrichedPartnership>(
    `${BASE_URL}/${companyId}/partnerships/${partnershipId}`
  );
};

/**
 * Companies the create-partnership picker may offer for this owner: every
 * company the caller can see except the owner and vendors the owner rejected
 * in onboarding. Unpaginated (the generic company list stopped at 20).
 */
export const listPartnerCandidates = async (companyId: string): Promise<Company[]> => {
  return apiFetch<Company[]>(`${BASE_URL}/${companyId}/partner-candidates`);
};

/**
 * Create a partnership
 */
export const createPartnership = async (
  companyId: string,
  dto: CreatePartnershipDto
): Promise<EnrichedPartnership> => {
  return apiFetch<EnrichedPartnership>(`${BASE_URL}/${companyId}/partnerships`, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
};

/** Network access level granted to a partner relationship. */
export type PartnerAccessLevel = 'NONE' | 'READ_ONLY' | 'READ_WRITE' | 'FULL';

/** Input for adding an existing user (by email) as a partner of the company. */
export interface AddPartnerUserDto {
  email: string;
  accessLevel: PartnerAccessLevel;
}

/**
 * Add an existing user as a partner of `companyId`. Network-scoped: ensures an
 * ACTIVE partnership carrying `accessLevel` and lets the user switch into the
 * company. Self-service; no platform-owner needed.
 */
export const addPartnerUser = async (
  companyId: string,
  dto: AddPartnerUserDto
): Promise<{ userId: string; ownerCompanyId: string; partnerCompanyId: string; accessLevel: PartnerAccessLevel }> => {
  return apiFetch<{ userId: string; ownerCompanyId: string; partnerCompanyId: string; accessLevel: PartnerAccessLevel }>(
    `${BASE_URL}/${companyId}/partner-users`,
    {
      method: 'POST',
      body: JSON.stringify(dto),
    }
  );
};

/**
 * Update a partnership
 */
export const updatePartnership = async (
  companyId: string,
  partnershipId: string,
  dto: UpdatePartnershipDto
): Promise<EnrichedPartnership> => {
  return apiFetch<EnrichedPartnership>(
    `${BASE_URL}/${companyId}/partnerships/${partnershipId}`,
    {
      method: 'PUT',
      body: JSON.stringify(dto),
    }
  );
};

/**
 * Terminate a partnership
 */
export const terminatePartnership = async (
  companyId: string,
  partnershipId: string,
  reason?: string
): Promise<void> => {
  return apiFetch<void>(
    `${BASE_URL}/${companyId}/partnerships/${partnershipId}`,
    {
      method: 'DELETE',
      body: reason ? JSON.stringify({ reason }) : undefined,
    }
  );
};

/**
 * Update grantedPlugins for a partnership (super-admin only).
 *
 * Endpoint: PUT /api/companies/partnerships/:partnershipId/granted-plugins —
 * the route is defined in the admin plugin's partnershipRoutes, which mount
 * under the /api/companies prefix (routes/index.ts), NOT /api/admin. This
 * client used to call /api/admin/... and every save from the company
 * Plugins tab 404'd.
 */
export const updatePartnershipGrantedPlugins = async (
  partnershipId: string,
  grantedPlugins: string[]
): Promise<EnrichedPartnership> => {
  return apiFetch<EnrichedPartnership>(
    `/api/companies/partnerships/${partnershipId}/granted-plugins`,
    {
      method: 'PUT',
      body: JSON.stringify({ grantedPlugins }),
    }
  );
};
